import { Box } from '@mui/system'
import { ReactNode, useState } from 'react'
import CoreContainer from './CoreContainer'
import { useResponsive } from 'hooks/useResponsive'
import styled from '@emotion/styled'
import { theme } from 'theme'
import ArrowUp from 'assets/icons-gray/arrow-diagonal-up.svg'
import { Fade, Link, Modal } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import MenuIcon from 'assets/icons-gray/menu.svg'
import CloseIcon from 'assets/icons-gray/close.svg'
import { CloseStyle } from './CoreModal'
import { RouteName } from 'constants/constants'
import { GitBook } from 'constants/externalLink'

const Tab = styled(Box)<{ selected?: boolean }>`
  border-bottom: ${({ selected }) =>
    selected
      ? `2px solid ${theme.palette.primary.main}`
      : '2px solid transparent'};
  color: ${({ selected }) =>
    selected ? theme.palette.text.primary : '#4C4C4C'};
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  padding: 1px;
  height: min-content;
  cursor: pointer;
  transition: all 0.4s;
  :hover {
    border-bottom: ${`2px solid ${theme.palette.primary.main}`};
  }
`

const Menu = styled.img`
  width: 28px;
  height: 28px;
  margin-left: 20px;
  cursor: pointer;
`

interface MenuProps {
  label: string
  path: RouteName
}
interface Props {
  logo: string
  menus?: MenuProps[]
  rightComponent?: ReactNode
}

const CoreNavBar = ({ logo, menus, rightComponent }: Props) => {
  const { isTablet } = useResponsive()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [openMenus, setOpenMenus] = useState(false)
  const hideMenu = [RouteName.default, RouteName.termAndCondition].includes(
    pathname as RouteName,
  )

  const handleOnOpenMenus = () => {
    setOpenMenus(true)
  }
  const handleOnCloseMenus = () => {
    setOpenMenus(false)
  }

  const Menus = menus && [
    ...menus.map((menu, idx) => (
      <Tab
        selected={menu.path === pathname}
        key={idx}
        onClick={() => {
          navigate(menu.path)
          handleOnCloseMenus()
        }}
      >
        {menu.label}
      </Tab>
    )),
    <Box sx={{ display: 'flex' }} key={menus.length}>
      <Tab>
        <Link
          href={GitBook.docs}
          target="_blank"
          mr="3px"
          color="#4C4C4C"
          sx={{
            '&:hover': {
              textDecoration: 'none',
            },
          }}
        >
          Docs
        </Link>
      </Tab>
      <img src={ArrowUp} alt="ArrowUp" width={16} height={16} />
    </Box>,
  ]

  return (
    <CoreContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '50px',
            alignItems: 'center',
            marginRight: 'auto',
          }}
        >
          <img
            src={logo}
            alt="edge-logo"
            width={48}
            height={48}
            onClick={() => navigate(RouteName.default)}
          />
          {!isTablet && !hideMenu && Menus?.map((menu) => menu)}
        </Box>
        {!hideMenu && <Box>{rightComponent}</Box>}
        {isTablet && !hideMenu && (
          <Menu src={MenuIcon} alt="MenuIcon" onClick={handleOnOpenMenus} />
        )}
      </Box>
      <MenuModal
        open={openMenus}
        onClose={handleOnCloseMenus}
        menus={Menus?.map((menu) => menu)}
      />
    </CoreContainer>
  )
}

export default CoreNavBar

const CustomNavbarBox = styled(Box)`
  width: 100vw;
  height: 100vh;
  background-color: #f6f6f6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 48px;
  transition: all 0.2s ease-in;
`

interface ModalProps {
  open: boolean
  onClose: () => void
  menus?: JSX.Element[]
}

const MenuModal = ({ open, onClose, menus }: ModalProps) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <CustomNavbarBox>
          <CloseStyle onClick={onClose}>
            <img src={CloseIcon} alt="CloseIcon" />
          </CloseStyle>
          {menus?.map((menu) => menu)}
        </CustomNavbarBox>
      </Fade>
    </Modal>
  )
}
